import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

const IMG_HERO = 'https://BelvoirCastle.b-cdn.net/Harvest/Harvest%20Day%20EY.webp';
const IMG_BUS = 'https://BelvoirCastle.b-cdn.net/Harvest/Gun%20Bus.webp';
const IMG_YVONNE = 'https://BelvoirCastle.b-cdn.net/Harvest/Yvonne%20Rivers.webp';
const IMG_GALLERY = 'https://BelvoirCastle.b-cdn.net/Harvest/WhatsApp%20Image%202026-09-25%20at%2015.24.42.webp';

const C = {
  navy: '#101d40',
  gold: '#C9963C',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
  muted: 'rgba(28,28,30,0.62)',
  border: 'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(520px, 82svh, 700px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 40rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => (p.$fill ? C.gold : 'rgba(255,255,255,0.11)')};
  color: #fff;
  border: 1px solid ${p => (p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)')};
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => (p.$border ? `1px solid ${C.border}` : 'none')};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => (p.$light ? '#fff' : C.navy)};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 40rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.78)' : C.muted)};
  margin: 0 0 clamp(1.25rem, 3vw, 1.75rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1.05fr 0.95fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const Photo = styled.div`
  border-radius: 14px;
  overflow: hidden;
  img {
    display: block;
    width: 100%;
    height: 100%;
    max-height: 520px;
    object-fit: cover;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${C.muted};
`;

const Notice = styled.div`
  background: rgba(201,150,60,0.08);
  border-left: 4px solid ${C.gold};
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${C.navy};
  font-weight: 600;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 0.85rem;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
  img {
    display: block;
    width: 100%;
    height: 100%;
    max-height: 460px;
    object-fit: cover;
    border-radius: 14px;
  }
`;

const HarvestDay = () => (
  <Page>
    <Helmet>
      <title>Harvest Day | Sunday 4 October 2026 | Belvoir Retail Village</title>
      <meta
        name="description"
        content="Harvest Day at Belvoir Retail Village on Sunday 4 October 2026. Live music with Yvonne Rivers, farm animals, Farm Shop tastings, complimentary Gun Bus tours, and a Harvest Service at 3:30pm."
      />
      <link rel="canonical" href={getCanonicalUrl('whats-on/harvest-day')} />
    </Helmet>

    <HeroWrap>
      <HeroImg src={IMG_HERO} alt="Harvest Day at Belvoir Retail Village" />
      <HeroOverlay />
      <HeroContent>
        <HeroKicker>Sunday 4 October 2026 · Retail Village</HeroKicker>
        <HeroTitle>Harvest <em>Day</em></HeroTitle>
        <HeroStrap>
          Celebrate the season at Belvoir&rsquo;s Retail Village this autumn.
        </HeroStrap>
        <BadgeRow>
          <Pill $fill>Free to attend</Pill>
          <Pill>11am – 3:30pm</Pill>
          <Pill>Refreshments included</Pill>
        </BadgeRow>
      </HeroContent>
    </HeroWrap>

    <Section $bg={C.ivory} $border>
      <Inner>
        <Kicker>The day</Kicker>
        <Divider />
        <SectionTitle>A village celebration <em>of the harvest</em></SectionTitle>
        <SectionLead>
          Enjoy live music, meet the animals, taste the best of the estate, and ride the Gun Bus around the grounds. The afternoon closes with a Harvest Service outside the Bistro.
        </SectionLead>
        <FeatureGrid>
          <FeatureCard>
            <FeatureTitle>Live music</FeatureTitle>
            <FeatureBody>Yvonne Rivers performs throughout the day, from 11am to 3pm.</FeatureBody>
          </FeatureCard>
          <FeatureCard>
            <FeatureTitle>Farm animals</FeatureTitle>
            <FeatureBody>Goats and sheep from Castle Farm take up residence in pens around the village.</FeatureBody>
          </FeatureCard>
          <FeatureCard>
            <FeatureTitle>Farm Shop tastings</FeatureTitle>
            <FeatureBody>Sample the best of the estate at tasting pop-ups in the Farm Shop.</FeatureBody>
          </FeatureCard>
          <FeatureCard>
            <FeatureTitle>Harvest Service</FeatureTitle>
            <FeatureBody>Led by the Belvoir Chaplain at 3:30pm outside the Bistro. Refreshments included.</FeatureBody>
          </FeatureCard>
        </FeatureGrid>
      </Inner>
    </Section>

    <Section $bg="#fff" $border>
      <Inner>
        <TwoCols>
          <div>
            <Kicker>11am – 3pm</Kicker>
            <Divider />
            <SectionTitle>Music with <em>Yvonne Rivers</em></SectionTitle>
            <SectionLead style={{ marginBottom: 0 }}>
              Live music runs through the heart of the day. Settle in around the Retail Village and enjoy Yvonne Rivers from 11am until 3pm.
            </SectionLead>
          </div>
          <Photo>
            <img src={IMG_YVONNE} alt="Yvonne Rivers performing at Harvest Day" />
          </Photo>
        </TwoCols>
      </Inner>
    </Section>

    <Section $bg={C.parchment} $border>
      <Inner>
        <TwoCols $cols="0.95fr 1.05fr">
          <Photo>
            <img src={IMG_BUS} alt="The Belvoir Gun Bus on an estate tour" />
          </Photo>
          <div>
            <Kicker>Complimentary</Kicker>
            <Divider />
            <SectionTitle>Gun Bus tours <em>of the estate</em></SectionTitle>
            <SectionLead>
              Tours pick you up from the Retail Village car park and run every 40 minutes from 11am to 3pm. A chance to see the estate as the harvest season turns.
            </SectionLead>
            <Notice>No booking is needed. Just join a departure from the Retail Village car park.</Notice>
          </div>
        </TwoCols>
      </Inner>
    </Section>

    <Section $bg={C.navy}>
      <Inner>
        <Kicker>3:30pm</Kicker>
        <Divider />
        <SectionTitle $light>Harvest Service <em>outside the Bistro</em></SectionTitle>
        <SectionLead $light>
          The day rounds off with a Harvest Service led by the Belvoir Chaplain at 3:30pm outside the Bistro. Refreshments are included. Food donations are welcome, and will be given to Grantham Food Bank.
        </SectionLead>
        <Gallery>
          <img src={IMG_GALLERY} alt="Harvest Day at Belvoir Retail Village" />
          <img src={IMG_HERO} alt="The Engine Yard dressed for Harvest Day" />
        </Gallery>
      </Inner>
    </Section>

    <Footer />
  </Page>
);

export default HarvestDay;
